import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';
import PillarLayout from './PillarLayout';

const CANONICAL = 'https://enbet-consulting.de/ki-im-stationaeren-handel';

const faqItems = [
  {
    q: 'Was bedeutet KI im stationären Handel konkret?',
    a: 'KI im stationären Handel meint den operativen Einsatz von Machine Learning und Sprachmodellen in Filialen, Lager, Kundenservice und Back-Office. Konkret: Bedarfsprognosen für Bestände, Schichtplanung auf Basis von Frequenzdaten, personalisierte Empfehlungen am Point of Sale, automatisierte Auswertung von Kundenfeedback. Es geht nicht um Roboter im Laden, sondern um Entlastung der Frontline-Teams für Service und Beratung.',
  },
  {
    q: 'Welche KI-Use-Cases liefern im Retail messbare Effekte?',
    a: 'Studien (KPMG, BCG, Universität St. Gallen) belegen messbare Ergebnisse vor allem in vier Bereichen: Bestandsoptimierung (bis 30 % weniger Fehlbestände, bis 25 % weniger Überbestände), Personalisierung (bis 35 % höhere Konversion bei datenbasiertem Empfehlungs-Setup), Personaleinsatzplanung (passgenauere Schichtpläne durch Frequenzprognosen) und Marketingautomatisierung (20–40 % Kostenreduktion bei höherer Wirksamkeit).',
  },
  {
    q: 'Was kostet KI-Einführung im Einzelhandel realistisch?',
    a: 'Die Spanne ist groß. Ein fundierter Standort-Check beginnt bei 850 € netto Festpreis (ENBET KI Ready Check). Eine ausgearbeitete Strategie mit Roadmap kostet 3.500 € netto als Festpreis. Die eigentliche Integration hängt vom Umfang ab – ein Pilot-Setup für einen klar definierten Use-Case ist in 4–6 Wochen möglich. Alle Preise verstehen sich zzgl. 19 % MwSt. Wichtig: Wer ohne Standortbestimmung Tools kauft, verbrennt typischerweise 20.000–50.000 € für Lösungen, die nicht greifen.',
  },
  {
    q: 'Warum scheitern KI-Projekte im stationären Handel so oft?',
    a: '77 % der Frontline-Teams sagen: Technologie ist vorhanden, aber falsch eingeführt. Die häufigsten Gründe: (1) Keine klaren Business-Ziele, KI wird als IT-Experiment geführt. (2) Fragmentierte Datenlandschaften – KI bekommt Mülldaten und gibt Mülldaten zurück. (3) Teams werden nicht enabled, Akzeptanz fehlt. (4) Tools werden ohne Standortbestimmung gekauft. Das ROAD-Framework (Requirements, Operationalize Data, Analytic Method, Deployment) adressiert genau diese Punkte vor dem ersten Tool-Kauf.',
  },
  {
    q: 'Wie startet ein Einzelhändler pragmatisch mit KI?',
    a: 'In dieser Reihenfolge: (1) Ehrlicher Standort-Check — wo sind die echten Verlustzeiten, wo lohnt KI wirklich? (2) Strategie mit klarer Roadmap und ROI-Kalkulation. (3) Pilot für genau einen Use-Case in 4–6 Wochen mit Live-Monitoring. (4) Schrittweise Skalierung nach belegtem Wirkungsnachweis. Wer diese Reihenfolge umdreht und mit Tool-Kauf startet, sieht in 6 Monaten meistens keinen ROI.',
  },
];

const longTailFaq = [
  {
    q: 'Wie lange dauert eine KI-Einführung im stationären Handel?',
    a: 'Der ehrliche Standort-Check (Modul 1) ist remote in 1–2 Wochen abgeschlossen. Der Strategie-Workshop (Modul 2) liefert nach 1–2 Wochen die Roadmap. Ein erster Pilot ist innerhalb von 4–6 Wochen live. Die volle Integration mit Skalierung läuft ab 3 Monaten.',
  },
  {
    q: 'Welche KI-Tools eignen sich für stationäre Geschäfte?',
    a: 'Das hängt vom Use-Case ab. Für Bestandsprognosen: spezialisierte Demand-Forecasting-Lösungen oder ML-Module der bestehenden Warenwirtschaft. Für Personalisierung: Empfehlungs-Engines mit DSGVO-konformem Setup. Für Mitarbeiter-Assistenz: LLM-basierte Wissens-Assistenten. Wichtig ist: Tool-Auswahl IMMER nach Bedarfsanalyse, nie nach Hype.',
  },
  {
    q: 'Kann KI den persönlichen Kundenkontakt im Laden ersetzen?',
    a: 'Nein – und das ist auch nicht das Ziel. KI im stationären Handel ist da am wirkungsvollsten, wo sie Teams von Routineaufgaben entlastet, damit mehr Zeit für Beratung und Service bleibt. Das ist der Kern unseres Entertain-Better-Ansatzes: KI als Werkzeug, der Mensch im Zentrum.',
  },
];

export default function PillarStationaererHandel() {
  const schemaLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        '@id': `${CANONICAL}#article`,
        headline: 'KI im stationären Handel – was funktioniert wirklich?',
        description:
          'Konkrete Use-Cases, realistische Kosten, ehrliche Einschätzung: KI im stationären Einzelhandel mit messbaren Effekten in Bestand, Personalisierung, Personaleinsatz und Service.',
        author: { '@id': 'https://enbet-consulting.de/#sebastian-funke' },
        publisher: { '@id': 'https://enbet-consulting.de/#organization' },
        mainEntityOfPage: CANONICAL,
        inLanguage: 'de-DE',
        about: 'KI im stationären Handel',
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Start', item: 'https://enbet-consulting.de/' },
          { '@type': 'ListItem', position: 2, name: 'KI im stationären Handel', item: CANONICAL },
        ],
      },
      {
        '@type': 'FAQPage',
        mainEntity: [...faqItems, ...longTailFaq].map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ],
  };

  return (
    <PillarLayout
      title="KI im stationären Handel – was funktioniert wirklich? | ENBET Consulting"
      description="KI im stationären Einzelhandel: konkrete Use-Cases, realistische Kosten, ehrliche Einschätzung – mit messbaren Effekten in Bestand, Personalisierung, Personaleinsatz und Service."
      canonical={CANONICAL}
      schemaLd={schemaLd}
    >
      {/* Hero */}
      <section className="bg-[#0a0a0a] pt-32 pb-20 md:pt-40 md:pb-28">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-[#befa0a] text-xs uppercase tracking-[0.3em] mb-5 font-bold"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            KI-Strategie Retail
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-white text-3xl md:text-5xl lg:text-6xl font-bold uppercase tracking-tight leading-[1.15] mb-6"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            KI im stationären Handel – was funktioniert wirklich?
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="text-white/70 text-base md:text-lg leading-relaxed max-w-2xl"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            Stationärer Einzelhandel sucht KI-Antworten, die im Tagesgeschäft ankommen — nicht auf Folien. Dieser Guide zeigt die Use-Cases mit echtem Hebel, die realistischen Kosten und die Stolperfallen, die teure Pilotfehler garantieren.
          </motion.p>
        </div>
      </section>

      {/* Kurzantwort (TL;DR) */}
      <section className="bg-[#f7f7f7] py-12 md:py-16">
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          <p className="text-[#befa0a] text-xs uppercase tracking-[0.25em] font-bold mb-3" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Kurzantwort
          </p>
          <p className="text-[#1f1f1f] text-lg md:text-xl leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
            KI im stationären Handel funktioniert, wenn sie auf Frontline-Probleme angewendet wird, für die es ehrliche Daten gibt: Bestandsprognosen, Schichtplanung, Personalisierung am Point of Sale, Service-Automatisierung. Sie scheitert, wenn Tools ohne Standort-Check gekauft werden. Eine fundierte Diagnose vor dem ersten Tool-Kauf entscheidet, ob aus 850 € Investition 50.000 € Wertschöpfung werden – oder 50.000 € versenktes Pilot-Budget.
          </p>
        </div>
      </section>

      {/* Content Sections — H2 als Frage + Antwort */}
      <section className="py-16 md:py-24">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 space-y-16">
          {faqItems.map((item, i) => (
            <article key={i} className="scroll-mt-24" id={`q-${i + 1}`}>
              <h2 className="text-[#1f1f1f] text-2xl md:text-3xl font-bold uppercase tracking-tight leading-tight mb-5" style={{ fontFamily: "'Oswald', sans-serif" }}>
                {item.q}
              </h2>
              <p className="text-[#1f1f1f]/75 text-base md:text-lg leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
                {item.a}
              </p>
            </article>
          ))}
        </div>
      </section>

      {/* Mini-FAQ */}
      <section className="bg-[#f5f5f5] py-16 md:py-24">
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          <h2 className="text-[#1f1f1f] text-2xl md:text-3xl font-bold uppercase tracking-tight mb-10" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Häufige Folgefragen
          </h2>
          <div className="space-y-8">
            {longTailFaq.map((item, i) => (
              <div key={i}>
                <h3 className="text-[#1f1f1f] text-lg md:text-xl font-bold uppercase tracking-tight mb-3" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {item.q}
                </h3>
                <p className="text-[#1f1f1f]/70 text-base leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {item.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Verwandte Pillars (Hub-Spoke) */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <h2 className="text-[#1f1f1f] text-2xl md:text-3xl font-bold uppercase tracking-tight mb-10" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Weiter vertiefen
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { to: '/ki-fuer-filialisten', label: 'KI für Filialisten', sub: 'Multi-Standort-Strukturen, Konsistenz, lokale Identität.' },
              { to: '/ki-strategie-retail', label: 'KI-Strategie Retail', sub: 'Konkrete Roadmap und ROI-Kalkulation aus dem Workshop.' },
              { to: '/ki-beratung-kassel', label: 'KI-Beratung Kassel', sub: 'Lokale Beratung aus Nordhessen für ganz Deutschland.' },
            ].map((link, i) => (
              <Link
                key={i}
                to={link.to}
                className="border border-[#e5e5e5] p-6 hover:border-[#befa0a] transition-colors flex flex-col gap-3"
              >
                <ArrowRight size={18} className="text-[#befa0a]" />
                <span className="text-[#1f1f1f] text-base font-bold uppercase tracking-tight" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {link.label}
                </span>
                <span className="text-[#1f1f1f]/60 text-sm leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {link.sub}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Inline-CTA */}
      <section className="bg-[#0a0a0a] py-16">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 text-center">
          <h2 className="text-white text-2xl md:text-3xl font-bold uppercase tracking-tight mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Direkt loslegen: kostenloser 20-Minuten Potenzial-Check
          </h2>
          <p className="text-white/60 text-base mb-8 max-w-xl mx-auto" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Wir schauen uns deinen Betrieb persönlich an und sagen ehrlich, wo KI sich rentiert – und wo nicht. Erste Bestandsaufnahme, kein Verkaufsdruck.
          </p>
          <Link
            to="/#booking"
            className="inline-flex items-center gap-2 bg-[#befa0a] text-[#0a0a0a] px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] hover:bg-white transition-colors"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            <Check size={16} /> Termin vereinbaren
          </Link>
        </div>
      </section>
    </PillarLayout>
  );
}
